import { useCallback, useRef, useState } from "react";
import { Upload, FileSpreadsheet, X, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";

export function FileUpload({ onDataLoaded }) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const parseFile = useCallback((f) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (jsonData.length === 0) {
          setError("Le fichier est vide");
          return;
        }

        const columns = Object.keys(jsonData[0]);
        const hasEmail = columns.some(
          (col) => col.toLowerCase() === "email"
        );

        if (!hasEmail) {
          setError("Le fichier doit contenir une colonne 'email'");
          return;
        }

        setFile(f);
        onDataLoaded({
          columns,
          rows: jsonData,
          fileName: f.name,
          totalRows: jsonData.length,
        });
      } catch {
        setError("Erreur lors de la lecture du fichier");
      }
    };
    reader.readAsArrayBuffer(f);
  }, [onDataLoaded]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) parseFile(f);
  }, [parseFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleChange = useCallback((e) => {
    const f = e.target.files[0];
    if (f) parseFile(f);
  }, [parseFile]);

  const clearFile = () => {
    setFile(null);
    setError(null);
    onDataLoaded(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <AnimatePresence mode="wait">
        {!file ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            data-testid="file-upload-dropzone"
            className={`
              border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer group relative overflow-hidden transition-all duration-300
              ${isDragging 
                ? "border-primary bg-primary/5" 
                : "border-zinc-200 hover:border-primary/50 bg-zinc-50/50"
              }
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleChange}
              data-testid="file-upload-input"
            />
            <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${isDragging ? "text-primary" : "text-zinc-300 group-hover:text-primary"}`} />
            <p className="text-sm font-medium text-zinc-700">
              Glissez votre fichier ici ou <span className="text-primary underline underline-offset-4">parcourir</span>
            </p>
            <p className="text-xs text-zinc-400 mt-2">
              Formats: .xlsx, .xls, .csv
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="file-loaded"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-3 p-4 border border-zinc-200 rounded-xl bg-white"
            data-testid="file-loaded-info"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">{file.name}</p>
              <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(1)} Ko</p>
            </div>
            <Badge variant="secondary" className="text-xs" data-testid="file-status-badge">
              Chargé
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-zinc-400 hover:text-zinc-700"
              onClick={clearFile}
              data-testid="file-clear-button"
            >
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
            data-testid="file-upload-error"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
